/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useRef, useState } from 'react';
import {
  CardNumberElement,
  CardMonthElement,
  CardYearElement,
  CardCvvElement,
  useRecurly,
  usePaymentContext,
  useHCaptchaContext,
} from '@fff-web/fff-payments';
import { showErrorToast } from '@fff-web/fff-ui-shared/Toast';
import Input from './Input';
import styles from './PaymentInfo.module.scss';
import useFormData from './hooks/useFormData';
import { useFriendContext } from '../../utils/ContextProviders/FriendContext';
import checkoutSecureLogo from './assets/img/checkout-secure-logo.png';
import SuccessfulAccountCreationModal from './SuccessfulAccountCreationModal';
import { useAppContext } from '../../utils/ContextProviders/AppContext';
import { handleSubmission } from './handleSubmission';
import useGetSocialEcommContracts from './hooks/useGetSocialEcommContracts';
import { isMobile, parseRecurlyErrors } from './paymentInfoUtils';
import { PaymentType } from '../../utils/Data/registerConnectorTypes';

export const CREDIT_CARD = 'CREDIT_CARD';

function CreditCard() {
  const formRef = useRef<HTMLFormElement>(null);
  const {
    formData, handleOnChange, formErrors, handleErrors, removeFormError,
  } = useFormData();
  const {
    firstName,
    lastName,
    postalCode,
  } = formData;
  const { billingMethod } = usePaymentContext();
  const { hCaptchaToken, setHCaptchaToken, setExecuteHCaptcha } = useHCaptchaContext();
  const socialEcommContracts = useGetSocialEcommContracts();
  const { friend, setFriend, avatar } = useFriendContext();
  const {
    goToStep, countries, viewSize,
  } = useAppContext();
  const recurly = useRecurly();
  const [submitting, setSubmitting] = useState(false);
  const [showAfterCreation, setShowAfterCreation] = useState(false);

  const handleRecurlyInput = (e: any, input: string) => {
    removeFormError(input);
    let error = {};
    switch (input) {
      case 'number':
        error = { number: 'Please enter valid credit card number.' };
        break;
      case 'month':
        error = { month: 'Please enter valid date.' };
        break;
      case 'year':
        error = { year: 'Please enter valid date.' };
        break;
      case 'cvv':
        error = { cvv: 'Please enter a valid CVV.' };
        break;
      default:
        break;
    }
    if (!e.focus && !e.empty && !e.valid) {
      handleErrors(error);
    }
  };

  const afterRegister = () => {
    setShowAfterCreation(true);
    setSubmitting(false);
  };

  const handleRegisterFailure = () => {
    setExecuteHCaptcha(false);
    setHCaptchaToken('');
  };

  const handleSubmit = () => {
    setSubmitting(true);
    if (formRef.current && hCaptchaToken) {
      recurly.token(formRef.current, async (err: any, token: any) => {
        if (err) {
          parseRecurlyErrors(err, setSubmitting, handleErrors);
          return showErrorToast(
            'Payment Update Failed',
            'Your payment information was not updated. Please try again.',
          );
        }

        return handleSubmission(
          token.id,
          friend,
          setFriend,
          goToStep,
          afterRegister,
          handleRegisterFailure,
          avatar,
          PaymentType.CREDIT_CARD,
          socialEcommContracts,
          countries,
          hCaptchaToken,
        );
      });
    }
  };

  function blockFormSubmission(e: any) {
    e.preventDefault();
  }

  const onTimeOut = () => {
    setShowAfterCreation(false);
  };

  useEffect(() => {
    if (hCaptchaToken && billingMethod === CREDIT_CARD) {
      handleSubmit();
    }
  }, [hCaptchaToken, billingMethod]);

  return (
    <div className={styles.creditCardContainer}>
      <form
        ref={formRef}
        onSubmit={blockFormSubmission}
      >
        <div className={styles.creditCard}>
          <div className={styles.nameInfo}>
            <div className={styles.inputWrapper}>
              <Input
                type="text"
                label="First Name"
                value={firstName}
                name="firstName"
                placeholder="First Name"
                handleInput={handleOnChange}
                error={formErrors.firstName}
                additionalCss={styles.firstName}
                dataRecurly="first_name"
              />
              {formErrors.firstName !== null
              && <span className={styles.inputErrorMsg}>{formErrors.firstName}</span>}
            </div>
            <div className={styles.inputWrapper}>
              <Input
                type="text"
                label="Last Name"
                value={lastName}
                name="lastName"
                placeholder="Last Name"
                handleInput={handleOnChange}
                error={formErrors.lastName}
                additionalCss={styles.lastName}
                dataRecurly="last_name"
              />
              {formErrors.lastName !== null
        && <span className={styles.inputErrorMsg}>{formErrors.lastName}</span>}
            </div>
          </div>
          <div className={styles.inputWrapper}>
            <label htmlFor="creditCardNumber" className={styles.label}>
              Credit Card Number
              <div className={formErrors.number ? styles.hasError : ''}>
                <CardNumberElement
                  style={{
                    placeholder: {
                      content: 'Credit Card Number',
                      fontFamily: '"Nunito Sans", sans-serif',
                      fontSize: '14px',
                      fontColor: 'black',
                      opacity: '0.4',
                      lineHeight: 1.29,
                    },
                    invalid: {
                      fontColor: '#cc484a',
                    },
                    fontFamily: '"Nunito Sans", sans-serif',
                    fontSize: '14px',
                  }}
                  onChange={(e: any) => handleRecurlyInput(e, 'number')}
                />
              </div>
            </label>
            {formErrors.number !== null
        && <span className={styles.recurlyError}>{formErrors.number}</span>}
          </div>
          <div className={styles.creditCardInfo}>
            <div>
              <div className={styles.expDate}>
                <label htmlFor="expMonth" className={styles.label}>
                  <span className={styles.labelText}>Expire Date</span>
                  <div className={formErrors.month ? styles.hasError : ''}>
                    <CardMonthElement
                      style={{
                        placeholder: {
                          content: 'MM',
                          fontFamily: '"Nunito Sans", sans-serif',
                          fontSize: '14px',
                          fontColor: 'black',
                          opacity: '0.4',
                          lineHeight: 1.29,
                        },
                        invalid: {
                          fontColor: '#cc484a',
                        },
                        fontFamily: '"Nunito Sans", sans-serif',
                        fontSize: '14px',
                      }}
                      onChange={(e: any) => handleRecurlyInput(e, 'month')}
                    />
                  </div>
                </label>
                <label htmlFor="expYear" className={styles.label}>
                  &nbsp;
                  <div className={formErrors.year ? styles.hasError : ''}>
                    <CardYearElement
                      style={{
                        placeholder: {
                          content: 'YY',
                          fontFamily: '"Nunito Sans", sans-serif',
                          fontSize: '14px',
                          fontColor: 'black',
                          opacity: '0.4',
                          lineHeight: 1.29,
                        },
                        invalid: {
                          fontColor: '#cc484a',
                        },
                        fontFamily: '"Nunito Sans", sans-serif',
                        fontSize: '14px',
                      }}
                      onChange={(e: any) => handleRecurlyInput(e, 'year')}
                    />
                  </div>
                </label>
                {(formErrors.month !== null || formErrors.year !== null)
            && <span className={styles.recurlyError}>Please enter valid date.</span>}
              </div>
            </div>
            <div className={styles.cvv}>
              <label htmlFor="cvv" className={styles.label}>
                CVV
                <div className={formErrors.cvv ? styles.hasError : ''}>
                  <CardCvvElement
                    style={{
                      placeholder: {
                        content: 'CVV',
                        fontFamily: '"Nunito Sans", sans-serif',
                        fontSize: '14px',
                        fontColor: 'black',
                        opacity: '0.4',
                        lineHeight: 1.29,
                      },
                      invalid: {
                        fontColor: '#cc484a',
                      },
                      fontFamily: '"Nunito Sans", sans-serif',
                      fontSize: '14px',
                    }}
                    onChange={(e: any) => handleRecurlyInput(e, 'cvv')}
                  />
                </div>
              </label>
              {formErrors.cvv !== undefined
            && <span className={styles.recurlyError}>{formErrors.cvv}</span>}
            </div>
            <div className={styles.inputWrapper}>
              <Input
                type="text"
                label="ZIP / Postal Code"
                value={postalCode}
                name="postalCode"
                placeholder="Billing Zipcode"
                handleInput={handleOnChange}
                error={formErrors.postalCode}
                additionalCss={styles.postalCode}
                dataRecurly="postal_code"
              />
              {formErrors.postalCode !== null
        && <span className={styles.inputErrorMsg}>{formErrors.postalCode}</span>}
            </div>
          </div>
          <Input
            type="text"
            label="Country"
            value={friend.address?.shipCountry ? friend.address.shipCountry : ''}
            name="country"
            placeholder="Country"
            handleInput={handleOnChange}
            error={formErrors.country}
            dataRecurly="country"
            hidden
          />
        </div>
        {friend.address.shipCountry !== 'GB' && (
        <p className={styles.disclaimer}>
          By
          {' '}
          {isMobile(viewSize) ? 'tapping' : 'clicking'}
          {' '}
          ‘Save &amp; Submit’, you agree to our
          {' '}
          <a href="https://legal.fabfitfun.com/#terms-of-use" target="_blank" rel="noreferrer">
            Terms of Use and Sale
          </a>
          {' '}
          and our
          {' '}
          <a href="https://legal.fabfitfun.com/#shop-with-friends-terms" target="_blank" rel="noreferrer">Shop with Friends Terms</a>
          .
        </p>
        )}
        {friend.address.shipCountry === 'GB' && (
        <p className={styles.disclaimer}>
          By signing up to take part in the non-checkout sale you agree to FabFitFun
          using your personal information to enable you to take part in the non-checkout sale,
          where everything in your basket will automatically be billed when the sale ends.
          For more information click
          {' '}
          <a href="https://legal.fabfitfun.com/#shop-with-friends-terms" target="_blank" rel="noreferrer">
            here
          </a>
          {' '}
          and our
          {' '}
          <a href="https://legal.fabfitfun.com/#privacy-policy" target="_blank" rel="noreferrer">
            Privacy Policy
          </a>
          .
        </p>
        )}
        <div className={styles.submitBtnWrapper}>
          <button
            id="submitPaymentBtn"
            type="submit"
            disabled={submitting}
            className={styles.submitBtn}
            onClick={() => setExecuteHCaptcha(true)}
          >
            Save &amp; Submit
          </button>
        </div>
        <div className={styles.checkoutLogoContainer}>
          <img src={checkoutSecureLogo} alt="Checkout secure" className={styles.checkoutSecureLogo} />
        </div>
      </form>
      { showAfterCreation && <SuccessfulAccountCreationModal onTimeOut={onTimeOut} /> }
    </div>
  );
}

export default CreditCard;
